import { EFinancingProgram, ELanguage, ELoanPurpose } from '@if/core/types'
import { InvitationHistoryEntryDto, InvitationHistoryFilterDto } from '@src/types/InvitationHistoryDto'
import { InvitationResultDto } from '@src/types/InvitationResultDto'
import { ECommunicationType } from '@src/types/InvitationSchema'
import { keepPreviousData, MutationFunction, useMutation, useQuery, UseQueryResult } from '@tanstack/react-query'
import { getApiClient } from '../api-client'

const SCOPE = 'invitations'
const HISTORY = 'history'

const fiveMinutes = 300000

const keysFactory = {
  history: (filters: InvitationHistoryFilterDto) => [{ scope: SCOPE, entity: HISTORY, ...filters }] as const,
}

export interface InvitationDto {
  firstName: string
  lastName: string
  languageId: ELanguage
  communicationType: ECommunicationType
  email: string | null
  mobilePhoneNumber: string | null
  financingProgramId: EFinancingProgram
  loanPurposeId?: ELoanPurpose | null
  resentFromInvitationId?: string | null
  /** Subsidiary merchant selected by a banner user; null for standard users. */
  merchantId?: string | null
  /** Optional financing amount (Personal program only); null when unset or for other programs. */
  amount?: number | null
}

const sendInvitation: MutationFunction<InvitationResultDto, InvitationDto> = async (dto: InvitationDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('/Invitation/Send', dto)
  return response.data as InvitationResultDto
}

export function useSendInvitation(): [
  MutationFunction<InvitationResultDto, InvitationDto>,
  boolean,
  boolean,
  () => void,
] {
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: sendInvitation,
    onSuccess: (data) => {
      return data
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

export async function sendApplicationInvitation(invitation: InvitationDto): Promise<InvitationResultDto> {
  const apiClient = getApiClient()
  const response = await apiClient.post('/Invitation/Send', invitation)
  return response.data as InvitationResultDto
}

export async function fetchInvitationHistory(
  filters: InvitationHistoryFilterDto,
): Promise<InvitationHistoryEntryDto[]> {
  const apiClient = getApiClient()
  const response = await apiClient.post('/Invitation/History', filters)
  return response.data as InvitationHistoryEntryDto[]
}

export function useInvitationHistory(
  filters: InvitationHistoryFilterDto,
  enabled: boolean,
  shouldPoll: boolean,
): UseQueryResult<InvitationHistoryEntryDto[]> {
  return useQuery({
    queryKey: keysFactory.history(filters),
    queryFn: () => fetchInvitationHistory(filters),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchInterval: shouldPoll ? fiveMinutes : undefined,
  })
}
